import React, { useState } from 'react';
import Breadcrumbs from '~/components/Breadcrumbs';
import CompaniesAlphabeticalList from '~/components/Companies/CompaniesAlphabeticalList';
import Container from '~/components/ui/Container';
import DefaultLayout from '~/layouts/DefaultLayout';
import type { ChainIndexProps } from '~/types/Chain';
import CompanyListItemTag from '~/components/Companies/CompanyListItemTag';
import { useChainFilters } from '~/components/Companies/useChainFilters';
import ChainFilters from '~/components/Companies/ChainFilters';
import { cn } from '~/helpers/utils';
import { Message } from '~/components/ui/Message';
import OpenFiltersButton from '~/components/Companies/OpenFiltersButton';

export const ChainIndex: React.FC<ChainIndexProps> = ({ chains }) => {
  const {
    setSearch,
    hasReviews,
    setHasReviews,
    hasAdvisors,
    setHasAdvisors,
    filteredChains,
  } = useChainFilters(chains);
  
  const [open, setOpen] = useState<boolean>(false);
  
  const breadcrumbLinks = [
    { name: 'Home', linkTo: '/' },
    { name: 'Ketens', linkTo: '/over/ketens' },
  ];

  return (
    <DefaultLayout breadcrumbs={<Breadcrumbs links={breadcrumbLinks} />}>
      <Container className="pb-16 md:pb-24">
        <div className="mb-8 grid grid-cols-1 gap-4 md:grid-cols-10 md:gap-10">
          <div className="md:col-span-3" />
          <div className="md:col-span-7">
            <h1 className="mb-2.5 font-avantgarde text-lg font-bold text-blue-700 md:mb-3 md:text-2xl">
              Ketens
            </h1>
            <h2 className="mb-4 font-avantgarde text-base font-light text-blue-500 md:mb-6 md:text-lg">
              Dit is een overzicht van alle ketens
            </h2>
          </div>
        </div>
        <div className="relative -mx-[2.5%] md:mx-0 mb-8">
          <div className="relative grid grid-cols-1 gap-4 md:grid-cols-10 md:gap-10">
            <div 
              className={cn(
                'z-10 absolute bg-white top-0 bottom-0 w-[80vw] transition-transform duration-300 ease-in-out shadow-lg p-6',
                'md:col-span-3 md:relative md:w-full md:translate-x-0 md:shadow-none md:p-0 md:duration-0',
                open ? 'translate-x-0' : '-translate-x-full',
              )}
            >
              <ChainFilters
                setSearch={setSearch}
                hasReviews={hasReviews}
                setHasReviews={setHasReviews}
                hasAdvisors={hasAdvisors}
                setHasAdvisors={setHasAdvisors}
              />
            </div>
            <div className="min-h-[370px] px-4 md:px-0 md:col-span-7">
              {filteredChains.length > 0 ? (
                <CompaniesAlphabeticalList
                  path="/over/keten"
                  companies={filteredChains}
                >
                  {({ statistics }) => (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {!!statistics.numberOfReviews && (
                        <CompanyListItemTag>
                          <span>
                            <strong>{statistics.numberOfReviews.toLocaleString('nl')}</strong>{' '}
                            Reviews
                          </span>
                        </CompanyListItemTag>
                      )}
                      {!!statistics.numberOfOffices && (
                        <CompanyListItemTag>
                          <span>
                            <strong>{statistics.numberOfOffices.toLocaleString('nl')}</strong>{' '}
                            Aangesloten kantoren
                          </span>
                        </CompanyListItemTag>
                      )}
                      {!!statistics.numberOfAdvisors && (
                        <CompanyListItemTag>
                          <span>
                            <strong>{statistics.numberOfAdvisors.toLocaleString('nl')}</strong>{' '}
                            Adviseurs
                          </span>
                        </CompanyListItemTag>
                      )}
                    </div>
                  )}
                </CompaniesAlphabeticalList>
              ) : (
                <Message>Er zijn helaas geen passende ketens gevonden.</Message>
              )}
            </div>
          </div>
          <OpenFiltersButton open={open} setOpen={setOpen} />
        </div>
      </Container>
    </DefaultLayout>
  );
};
